import React, { useCallback, useRef, useState } from 'react';
import { X } from 'lucide-react';
import { TABLE_MAX_CELL_CHARS, normalizeCell, setCell } from '../../utils/messageTable';

/**
 * 입력창(컴포저) 안의 편집 가능한 표.
 *
 * 말풍선의 MessageTable과 **일부러 분리**했다. 그쪽은 renderCell 주입(링크·멘션·검색 하이라이트)과
 * React.memo가 성능 계약이라, 편집용 <input>을 renderCell로 밀어 넣으면 셀마다 콜백 참조가 달라져
 * memo가 즉시 죽는다. 대신 테두리·머리글 음영·줄무늬 값은 그쪽과 같은 리터럴을 쓴다
 * (Tailwind JIT가 조립 클래스명을 못 읽으므로 공용 모듈로 빼지 않는다 — MessageTable.tsx:24 참고).
 *
 * ⚠ 가로 스크롤에 MessageTable의 JS scrollLeft 우회를 복사하지 말 것. 그건 MessageItem 행의
 *   touch-action: pan-y 조상 때문에 생긴 것이고 컴포저에는 그 조상이 없다.
 */
export interface ComposerTableGridProps {
  rows: string[][];
  hasHeader: boolean;
  align: ('left' | 'right')[];
  darkMode?: boolean;
  /** 셀 값이 바뀐 새 격자. 호출부가 표 판정이 풀리는지 검사한 뒤 반영한다 */
  onChange: (rows: string[][]) => void;
  /** 표 카드 제거 */
  onRemove: () => void;
  /** 첫 행에서 ↑ — 위 텍스트 블록으로 */
  onLeaveUp?: () => void;
  /** 마지막 행에서 ↓ 또는 Enter — 아래 텍스트 블록으로 */
  onLeaveDown?: () => void;
  /** 이모지 삽입 등이 "지금 포커스된 셀"을 알아야 한다 */
  onCellFocus?: (r: number, c: number, el: HTMLInputElement) => void;
  onCellBlur?: () => void;
}

const SURFACE = {
  dark: { card: 'border-gray-600 bg-gray-800', cell: 'border-gray-600', th: 'bg-gray-700', text: 'text-gray-100', muted: 'text-gray-400' },
  light: { card: 'border-slate-200 bg-white', cell: 'border-slate-200', th: 'bg-slate-100', text: 'text-slate-700', muted: 'text-slate-400' },
} as const;

const ComposerTableGridComponent: React.FC<ComposerTableGridProps> = ({
  rows, hasHeader, align, darkMode, onChange, onRemove, onLeaveUp, onLeaveDown, onCellFocus, onCellBlur,
}) => {
  const surface = darkMode ? SURFACE.dark : SURFACE.light;
  const gridRef = useRef<HTMLDivElement>(null);
  const cols = rows[0] ? rows[0].length : 0;

  /**
   * ⚠ 지금 편집 중인 셀만 로컬 값으로 그린다.
   *
   * rows는 본문 문자열에서 매번 다시 계산되고 그 과정에서 normalizeCell이 앞뒤 공백을 지운다.
   * 그래서 prop만 믿고 그리면 사용자가 공백을 칠 때마다 즉시 되돌아가 **낱말 사이에 공백을
   * 넣을 수 없다.** 포커스가 있는 동안에는 친 그대로 보여주고, blur에서 정규화한다.
   */
  const [draft, setDraft] = useState<{ r: number; c: number; value: string } | null>(null);
  const valueAt = (r: number, c: number) =>
    draft && draft.r === r && draft.c === c ? draft.value : rows[r][c];

  const focusCell = useCallback((r: number, c: number) => {
    const el = gridRef.current?.querySelector<HTMLInputElement>(`[data-r="${r}"][data-c="${c}"]`);
    if (el) {
      el.focus();
      const end = el.value.length;
      el.setSelectionRange(end, end);
    }
  }, []);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>, r: number, c: number) => {
    // ⚠ <form> 안의 <input>은 Enter로 암묵 submit을 일으킨다. 막지 않으면 셀을 고치다 메시지가 나간다.
    if (e.key === 'Enter') {
      e.preventDefault();
      if (e.nativeEvent.isComposing || e.keyCode === 229) return; // 한글 조합 확정 Enter
      if (r + 1 < rows.length) focusCell(r + 1, c);
      else onLeaveDown?.();
      return;
    }
    if (e.nativeEvent.isComposing || e.keyCode === 229) return;
    // ←/→는 절대 가로채지 않는다 — 셀 안 캐럿 이동이어야 한다
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (r + 1 < rows.length) focusCell(r + 1, c);
      else onLeaveDown?.();
      return;
    }
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      if (r > 0) focusCell(r - 1, c);
      else onLeaveUp?.();
      return;
    }
    if (e.key === 'Escape') {
      e.preventDefault();
      e.currentTarget.blur();
    }
    // Tab은 손대지 않는다 — input의 DOM 순서가 이미 행 우선이라 네이티브 이동이 곧 셀 이동이다
  };

  // 셀 붙여넣기는 그 셀 안에서만 끝난다. 여러 셀로 흘려 넣지 않는다(행·열 기하 변경은 범위 밖)
  const handleCellPaste = (e: React.ClipboardEvent<HTMLInputElement>, r: number, c: number) => {
    const plain = e.clipboardData?.getData('text/plain');
    if (!plain) return;
    e.preventDefault();
    const el = e.currentTarget;
    const start = el.selectionStart ?? el.value.length;
    const end = el.selectionEnd ?? start;
    const merged = el.value.slice(0, start) + normalizeCell(plain) + el.value.slice(end);
    setDraft({ r, c, value: merged });
    onChange(setCell(rows, r, c, merged));
  };

  const body = hasHeader ? rows.slice(1) : rows;
  const headRow = hasHeader ? rows[0] : null;

  const cell = (value: string, r: number, c: number, isHead: boolean) => (
    <div className="relative">
      {/* 폭 결정용 그림자 텍스트 — input에는 고유 폭이 없어 이게 없으면 모든 열이 같은 폭이 된다 */}
      <span
        aria-hidden="true"
        className="invisible block whitespace-pre px-2 py-1 max-w-[16rem] overflow-hidden"
      >
        {value || ' '}
      </span>
      <input
        type="text"
        data-r={r}
        data-c={c}
        value={value}
        maxLength={TABLE_MAX_CELL_CHARS}
        // 타이핑 중에는 정규화하지 않는다 — 조합 중 값을 바꾸면 IME가 리셋된다.
        // setCell이 탭·개행만 접고, 완전한 정규화(앞뒤 공백 제거)는 blur에서 한다.
        onChange={ev => {
          setDraft({ r, c, value: ev.target.value });
          onChange(setCell(rows, r, c, ev.target.value));
        }}
        onKeyDown={ev => handleKeyDown(ev, r, c)}
        onPaste={ev => handleCellPaste(ev, r, c)}
        onFocus={ev => onCellFocus?.(r, c, ev.currentTarget)}
        onBlur={ev => {
          const trimmed = normalizeCell(ev.currentTarget.value);
          setDraft(null);
          if (trimmed !== rows[r][c]) onChange(setCell(rows, r, c, trimmed));
          onCellBlur?.();
        }}
        className={`table-cell-text absolute inset-0 w-full bg-transparent border-none outline-none focus:ring-0 px-2 py-1 text-xs md:text-sm ${
          isHead ? 'font-semibold' : ''
        } ${align[c] === 'right' ? 'text-right' : ''} ${surface.text}`}
      />
    </div>
  );

  return (
    <div ref={gridRef} className={`rounded-lg border ${surface.card}`}>
      <div className={`flex items-center justify-between gap-2 px-2 py-1 border-b ${surface.cell}`}>
        <span className={`text-xs ${surface.muted}`}>표 {rows.length}행 {cols}열</span>
        <button
          type="button"
          onClick={onRemove}
          aria-label="표 제거"
          title="표 제거"
          className={`p-1 rounded transition-colors ${surface.muted} hover:text-brand-600 hover:bg-brand-50`}
        >
          <X size={14} />
        </button>
      </div>
      {/* 표가 커도 컴포저가 화면을 삼키지 않게 높이를 묶는다 (소프트 키보드가 뜨면 index.css가 더 줄인다) */}
      <div className="composer-table-card overflow-auto max-h-[40vh] md:max-h-60">
        <table className="w-full border-collapse">
          {headRow && (
            <thead>
              <tr>
                {headRow.map((_v, c) => (
                  <th key={c} className={`border p-0 align-top ${surface.cell} ${surface.th}`}>
                    {cell(valueAt(0, c), 0, c, true)}
                  </th>
                ))}
              </tr>
            </thead>
          )}
          <tbody>
            {body.map((row, i) => {
              const r = hasHeader ? i + 1 : i;
              return (
                <tr key={r}>
                  {row.map((_v, c) => (
                    <td key={c} className={`border p-0 align-top ${surface.cell}`}>
                      {cell(valueAt(r, c), r, c, false)}
                    </td>
                  ))}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export const ComposerTableGrid = ComposerTableGridComponent;
